import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  withStyles,
} from "@material-ui/core";
import { CheckCircleOutline, Email, Phone, Search } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { redtailAction } from "../../../../../../actions";
import { formatPhone } from "../../../../../../utilities/format";
import { BaseTooltip } from "../../../../../../components/BaseComponents";


const styles = (theme) => ({
  pinned: {
    margin: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
  },
  search: {
    minWidth: 400,
    "& input::placeholder": {
      fontSize: theme.typography.fontSize,
      paddingLeft: theme.spacing(1),
    },
  },
  message: {
    fontSize: theme.typography.fontSize,
  },
  table: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& th": {
      color: theme.palette.primary.main,
    },
    "& th, & td": {
      fontSize: theme.typography.fontSize,
      padding: theme.spacing(1),
    },
  },
  address: {
    fontSize: theme.typography.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    fontStyle: "normal",
    lineHeight: theme.typography.base.lineHeight,
    "& svg": {
      fontSize: theme.typography.base.fontSize,
      marginRight: theme.spacing(2),
      color: theme.palette.grey[700],
      lineHeight: theme.typography.base.lineHeight,
    },
  },
  footer: {
    marginBottom: theme.spacing(2),
    "& button": {
      marginLeft: theme.spacing(2),
    },
  },
});

const RedtailImportContacts = ({ classes, onClose }) => {

  const searchRef = useRef();

  const dispatch = useDispatch();

  const [
    requestSuccess,
    pending,
    runningJob,
    message,
    families,
    importedFamilies,
    errorFamilies,
    familiesToImport,
  ] = useSelector(({ redtail }) => [
    redtail.requestSuccess,
    redtail.pending,
    redtail.runningJob,
    redtail.message,
    redtail.families,
    redtail.importedFamilies,
    redtail.errorFamilies,
    redtail.familiesToImport,
  ]);

  const [checkedItems, setCheckedItems] = useState({});
  const [checkedItemsCount, setCheckedItemsCount] = useState(0);

  useEffect(() => {
    dispatch(redtailAction.clearFamilies());
    dispatch(redtailAction.clearJob());
    dispatch(redtailAction.getFamilies());
  }, []);

  useEffect(() => {
    setCheckedItemsCount(mapFamilyIds(checkedItems).length)
  }, [checkedItems]);

  // Reset checkbox once job is queued
  useEffect(() => {
    setCheckedItems({});
  }, [runningJob]);

  const handleKeyUp = (e) => {
    if (e.keyCode === 13) processSearch(e.target.value);
  };

  const processSearch = (searchString) => {
    if(searchString === '') {
      dispatch(redtailAction.getFamilies());
      return;
    }
    dispatch(redtailAction.searchFamilies(searchString));
  }

  const handleSelectFamily = (e) => {
    const obj = {
      ...checkedItems,
      [e.target.name]: e.target.checked,
    }
    setCheckedItems(obj);
  };

  const mapFamilyIds = (obj) => {
    if (Object.keys(obj).length === 0) {
      return [];
    }
    return Object.keys(obj)
      .filter((key) => obj[key] === true)
      .map((_) => Number(_));
  };

  const handleImportContacts = () => {
    // Skip if checked item is empty
    if (checkedItemsCount === 0) return;
    const familyIds = mapFamilyIds(checkedItems);
    const payload = { familyIds };
    const searchString = searchRef.current.value;
    if(searchString === '') {
      dispatch(redtailAction.queueImportJob(payload));
      return;
    }
    dispatch(redtailAction.queueImportJob(payload, searchString));
  };

  const columns = [
    {
      field: "imported",
      headerName: "",
    },
    {
      field: "name",
      headerName: "Household",
    },
    {
      field: "client",
      headerName: "Client",
    },
    {
      field: "spouse",
      headerName: "Spouse",
    },
    {
      field: "contact",
      headerName: "Contact",
    },
    {
      field: "address",
      headerName: "Address",
    },
  ];

  return (
    <React.Fragment>
      <Box className={classes.pinned}>
        {message && (
          <Alert
            severity={requestSuccess ? "success" : "error"}
            icon={pending && <CircularProgress size={16} />}
            className={classes.message}
          >
            {message}
          </Alert>
        )}
        <TextField
          placeholder="Search client name, phone number or email"
          inputRef={searchRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="search"
                  color="primary"
                  size="small"
                  onClick={() => processSearch(searchRef.current.value)}
                  disabled={pending || runningJob}
                >
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyUp={handleKeyUp}
          disabled={pending || runningJob}
          className={classes.search}
        />
      </Box>
      <DialogContent>
        <Table className={classes.table} aria-label="Contact list">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field}>{column.headerName}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {families &&
              families.map((family) => (
                <TableRow key={family.id}>
                  <TableCell component="th" scope="row">
                    <Grid
                      spacing={1}
                      container
                      direction="column"
                      justify="center"
                      alignItems="center"
                    >
                      {family.imported || importedFamilies.includes(family.id) ? (
                        <React.Fragment>
                          <CheckCircleOutline color="primary" />
                          <Typography color="primary" variant={"button"}>
                            DONE
                          </Typography>
                        </React.Fragment>
                      ) : familiesToImport.includes(family.id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Checkbox
                          onChange={handleSelectFamily}
                          name={`${family.id}`}
                          checked={!!checkedItems[family.id]}
                          color="primary"
                          disabled={pending || runningJob}
                        />
                      )}
                    </Grid>
                  </TableCell>
                  <TableCell>
                    {family.name}
                    {errorFamilies.includes(family.id) && (
                      <BaseTooltip
                        labelComponent={
                          <Typography variant="body1" style={{ color: "red" }}>
                            (Error)
                          </Typography>
                        }
                        helperText={family.errorMessage ? family.errorMessage : "Please try importing the contact again."}
                        boxProps={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{family.client}</TableCell>
                  <TableCell>{family.spouse}</TableCell>
                  <TableCell>
                    {family.contact && (
                      <address className={classes.address}>
                        {family.contact.email && (
                          <Grid
                            container
                            direction="row"
                            justify="flex-start"
                            alignItems="center"
                          >
                            <Email />
                            {family.contact.email}
                          </Grid>
                        )}
                        {family.contact.phone && (
                          <Grid
                            container
                            direction="row"
                            justify="flex-start"
                            alignItems="center"
                          >
                            <Phone />
                            {formatPhone(family.contact.phone)}
                          </Grid>
                        )}
                      </address>
                    )}
                  </TableCell>
                  <TableCell>
                    {family.address && (
                      <address className={classes.address}>
                        {family.address.street_address}
                        {family.address.secondary_address && (
                          <React.Fragment>
                            , {family.address.secondary_address}
                          </React.Fragment>
                        )}
                        <br />
                        {family.address.city}, {family.address.state}{" "}
                        {family.address.zip}
                      </address>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
          className={classes.footer}
        >
          <Box>
            {!runningJob && (
              <Typography>
                {checkedItemsCount} Contact{checkedItemsCount > 1 && "s"}{" "}
                selected
              </Typography>
            )}
          </Box>
          <Box>
            {pending || runningJob ? (
              <Button
                onClick={onClose}
                size="medium"
                color="primary"
                variant="contained"
              >
                Close
              </Button>
            ) : (
              <React.Fragment>
                {importedFamilies.length > 0 ? (
                  <Button onClick={onClose} color="primary" variant="outlined">
                    Done
                  </Button>
                ) : (
                  <Button onClick={onClose} size="medium">
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleImportContacts}
                  size="medium"
                  color="primary"
                  variant="contained"
                  disabled={checkedItemsCount === 0}
                >
                  Import
                </Button>
              </React.Fragment>
            )}
          </Box>
        </Grid>
      </DialogActions>
    </React.Fragment>
  );
};

RedtailImportContacts.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default withStyles(styles)(RedtailImportContacts);



// WEBPACK FOOTER //
// ./src/containers/Dashboard/components/ImportClientModal/components/RedtailImportContacts/index.jsx